import React, { useState, useRef, useEffect } from "react";
import { Mail, X } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const CRITERIA = [
  { key: "criteria_8chars", test: (p) => p.length >= 8 },
  { key: "criteria_uppercase", test: (p) => /[A-Z]/.test(p) },
  { key: "criteria_number", test: (p) => /[0-9]/.test(p) },
];

function getAge(birthdate) {
  const birth = new Date(birthdate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function LoginModal({ onClose, onLogin, t = (k) => k, lang = "fr", initialTab = "login" }) {
  const [tab, setTab] = useState(initialTab);
  const [registerStep, setRegisterStep] = useState(1);

  // Forgot password
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    if (!forgotSent) return;
    setResendCountdown(30);
    const interval = setInterval(() => {
      setResendCountdown((c) => {
        if (c <= 1) { clearInterval(interval); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [forgotSent]);

  // Fields
  const [firstname, setFirstname] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptCgu, setAcceptCgu] = useState(false);
  const [readPrivacy, setReadPrivacy] = useState(false);
  const [readCgu, setReadCgu] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);

  // Email suggestions
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const [emailHistory, setEmailHistory] = useState(
    () => JSON.parse(localStorage.getItem("rms_email_history") || "[]")
  );
  const emailInputRef = useRef(null);
  const emailSuggestionsRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (
        !emailInputRef.current?.contains(e.target) &&
        !emailSuggestionsRef.current?.contains(e.target)
      ) setShowEmailSuggestions(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const saveEmail = (value) => {
    if (!value.trim()) return;
    const next = [value, ...emailHistory.filter((e) => e.toLowerCase() !== value.toLowerCase())].slice(0, 8);
    setEmailHistory(next);
    localStorage.setItem("rms_email_history", JSON.stringify(next));
  };

  const removeEmail = (value) => {
    const next = emailHistory.filter((e) => e !== value);
    setEmailHistory(next);
    localStorage.setItem("rms_email_history", JSON.stringify(next));
  };

  const filteredEmails = emailHistory.filter((e) =>
    e.toLowerCase().includes(email.toLowerCase())
  );

  const resetRegister = () => {
    setFirstname(""); setBirthdate(""); setEmail("");
    setPassword(""); setConfirm(""); setError("");
    setAcceptPrivacy(false); setAcceptCgu(false);
    setReadPrivacy(false); setReadCgu(false); setRegisterStep(1);
  };

  // ── Étape 1 → 2 (prénom + date) ──
  const nextStep1 = () => {
    if (!firstname.trim()) return setError(t("lm_error_firstname"));
    if (!birthdate) return setError(t("lm_error_birthdate"));
    const age = getAge(birthdate);
    if (age < 15) return setError(t("lm_error_age"));
    setError("");
    setRegisterStep(2);
  };

  // ── Étape 2 → 3 (email + mdp) ──
  const nextStep2 = async () => {
    if (!email) return setError(t("lm_error_email"));
    const failed = CRITERIA.filter((c) => !c.test(password));
    if (failed.length > 0) return setError(`Mot de passe invalide : ${t(failed[0].key).toLowerCase()}.`);
    if (password !== confirm) return setError("Les mots de passe ne correspondent pas.");
    try {
      await axios.post(`${API}/auth/check-email`, { email });
    } catch (e) {
      if (e.response?.status === 409) return setError(t("lm_error_email_taken"));
    }
    setError("");
    setRegisterStep(3);
  };

  // ── Soumission finale ──
  const submit = async () => {
    if (tab === "login") {
      if (!email || !password) return setError(t("lm_error_fields"));
    } else {
      if (!acceptPrivacy || !acceptCgu) return setError(t("lm_error_legal"));
    }

    setError("");
    setLoading(true);
    try {
      const endpoint = tab === "login" ? "/auth/login" : "/auth/register";
      const body = tab === "register"
        ? { email, password, firstname: firstname.trim(), ref: localStorage.getItem("rms_ref") || undefined, lang }
        : { email, password };
      const res = await axios.post(`${API}${endpoint}`, body);
      saveEmail(email);
      if (res.data.pending_verification) {
        setPendingEmail(email);
      } else {
        onLogin(res.data.access_token || res.data.token, res.data.user);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Erreur, réessaie.";
      if (e.response?.status === 403 && detail.includes("email")) {
        setPendingEmail(email);
      } else {
        setError(detail);
      }
    }
    setLoading(false);
  };

  // ── Indicateur de progression ──
  const StepDots = () => (
    <div className="register-steps">
      {[1, 2, 3].map((s) => (
        <div key={s} className={`register-step-dot${registerStep === s ? " active" : registerStep > s ? " done" : ""}`}>
          {registerStep > s ? "✓" : s}
        </div>
      ))}
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}><X size={16} /></button>
        <h2 className="modal-title">{t("lm_title")}</h2>

        {pendingEmail ? (
          <div style={{ textAlign: "center", padding: "0.75rem 0" }}>
            <div style={{ marginBottom: '0.75rem', color: "var(--gold)" }}><Mail size={36} /></div>
            <p style={{ fontWeight: 800, fontSize: '0.9375rem', marginBottom: '0.5rem' }}>{t("lm_verify_title")}</p>
            <p style={{ color: "var(--muted)", fontSize: '0.8125rem', marginBottom: '1.25rem' }}>
              {t("lm_verify_pre")}<br />
              <strong style={{ color: "var(--accent-blue)" }}>{pendingEmail}</strong>
            </p>
            <button
              className="cta cta-ghost wide"
              style={{ fontSize: '0.8125rem', marginBottom: '0.5rem' }}
              onClick={async () => {
                try {
                  await axios.post(`${API}/auth/resend-verification`, { email: pendingEmail, password });
                  setError("");
                } catch (e) { setError(t("lm_resend_error")); }
              }}
            >
              {t("lm_resend")}
            </button>
            {error && <div className="error-msg" style={{ marginTop: '0.5rem' }}>{error}</div>}
          </div>
        ) : (
          <>
            <div className="modal-tabs">
              <button
                className={`modal-tab ${tab === "login" ? "active" : ""}`}
                onClick={() => { setTab("login"); resetRegister(); setForgotMode(false); }}
              >
                {t("lm_tab_login")}
              </button>
              <button
                className={`modal-tab ${tab === "register" ? "active" : ""}`}
                onClick={() => { setTab("register"); setEmail(""); setPassword(""); setConfirm(""); setError(""); setRegisterStep(1); }}
              >
                {t("lm_tab_register")}
              </button>
            </div>

            {/* ── INSCRIPTION multi-étapes ── */}
            {tab === "register" && (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: '0.375rem', background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.35)", borderRadius: '0.5rem', padding: "0.4375rem 0.75rem", marginBottom: '0.75rem', fontSize: '0.8125rem', color: "var(--gold)", fontWeight: 600 }}>
                  ✦ {t("lm_signup_premium_pitch")}
                </div>
                <StepDots />

                {registerStep === 1 && (
                  <>
                    <p className="register-step-label">{t("lm_step1_welcome")}</p>
                    <input
                      className="rm-input"
                      style={{ width: "100%", marginBottom: '0.625rem' }}
                      placeholder={t("lm_firstname_placeholder")}
                      type="text"
                      value={firstname}
                      autoFocus
                      onChange={(e) => setFirstname(e.target.value)}
                    />
                    <p className="register-step-sub" style={{ marginBottom: '0.375rem' }}>{t("lm_birthdate_sub")}</p>
                    <input
                      className="rm-input"
                      style={{ width: "100%", marginBottom: '0.875rem' }}
                      type="date"
                      value={birthdate}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={(e) => setBirthdate(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && nextStep1()}
                    />
                    {error && <div className="error-msg" style={{ marginBottom: '0.625rem' }}>{error}</div>}
                    <button className="cta cta-primary wide" onClick={nextStep1}>
                      {t("lm_continue")}
                    </button>
                  </>
                )}

                {registerStep === 2 && (
                  <>
                    <p className="register-step-label">{t("lm_step2_title", firstname)}</p>
                    <input
                      className="rm-input"
                      style={{ width: "100%", marginBottom: '0.625rem' }}
                      placeholder={t("lm_email_placeholder")}
                      type="email"
                      value={email}
                      autoFocus
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <div style={{ position: "relative", marginBottom: '0.375rem' }}>
                      <input
                        className="rm-input"
                        style={{ width: "100%", paddingRight: '2.375rem' }}
                        type={showPwd ? "text" : "password"}
                        placeholder={t("lm_password_placeholder")}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <button type="button" onClick={() => setShowPwd(v => !v)} style={{ position: "absolute", right: '0.625rem', top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--muted)", fontSize: '1rem', padding: 0 }}>{showPwd ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg> : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}</button>
                    </div>
                    <div style={{ display: "flex", gap: '0.625rem', marginBottom: '0.625rem', flexWrap: "wrap" }}>
                      {CRITERIA.map((c) => {
                        const ok = c.test(password);
                        return (
                          <span key={c.key} style={{ fontSize: '0.6875rem', fontWeight: 700, color: ok ? "#5bd4ff" : "var(--muted)" }}>
                            {ok ? "✓" : "·"} {t(c.key)}
                          </span>
                        );
                      })}
                    </div>
                    <input
                      className="rm-input"
                      style={{ width: "100%", marginBottom: '0.625rem' }}
                      type={showPwd ? "text" : "password"}
                      placeholder={t("lm_confirm_placeholder")}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && submit()}
                    />
                    {error && <div className="error-msg" style={{ marginBottom: '0.625rem' }}>{error}</div>}
                    <button className="cta cta-primary wide" onClick={nextStep2}>
                      {t("lm_continue")}
                    </button>
                    <button className="cta cta-ghost wide" style={{ marginTop: '0.5rem', fontSize: '0.75rem' }} onClick={() => { setRegisterStep(1); setError(""); }}>
                      {t("lm_back")}
                    </button>
                  </>
                )}

                {registerStep === 3 && (
                  <>
                    <p className="register-step-label">{t("lm_step3_title")}</p>
                    <div className="legal-checks">
                      <div className="legal-check-row">
                        <input
                          type="checkbox"
                          checked={acceptPrivacy}
                          disabled={!readPrivacy}
                          onChange={(e) => setAcceptPrivacy(e.target.checked)}
                          style={{ opacity: readPrivacy ? 1 : 0.35 }}
                        />
                        <span>
                          {t("lm_accept_privacy")}{" "}
                          <button className="legal-check-link" onClick={() => { setReadPrivacy(true); window.dispatchEvent(new CustomEvent("openLegal", { detail: "privacy" })); }}>
                            {t("lm_privacy_link")}
                          </button>
                          {!readPrivacy && <span className="legal-check-hint">{t("lm_read_first")}</span>}
                        </span>
                      </div>
                      <div className="legal-check-row">
                        <input
                          type="checkbox"
                          checked={acceptCgu}
                          disabled={!readCgu}
                          onChange={(e) => setAcceptCgu(e.target.checked)}
                          style={{ opacity: readCgu ? 1 : 0.35 }}
                        />
                        <span>
                          {t("lm_accept_cgu")}{" "}
                          <button className="legal-check-link" onClick={() => { setReadCgu(true); window.dispatchEvent(new CustomEvent("openLegal", { detail: "cgu" })); }}>
                            {t("lm_cgu_link")}
                          </button>
                          {!readCgu && <span className="legal-check-hint">{t("lm_read_first")}</span>}
                        </span>
                      </div>
                    </div>
                    {error && <div className="error-msg" style={{ marginBottom: '0.625rem' }}>{error}</div>}
                    <button className="cta cta-primary wide" onClick={submit} disabled={loading || !acceptPrivacy || !acceptCgu}>
                      {loading ? "..." : t("lm_create_account")}
                    </button>
                    <button className="cta cta-ghost wide" style={{ marginTop: '0.5rem', fontSize: '0.75rem' }} onClick={() => { setRegisterStep(2); setError(""); }}>
                      {t("lm_back")}
                    </button>
                  </>
                )}
              </>
            )}

            {/* ── CONNEXION ── */}
            {tab === "login" && (
              <>
                {forgotMode ? (
                  forgotSent ? (
                    <div style={{ textAlign: "center", padding: "0.5rem 0" }}>
                      <div style={{ marginBottom: '0.5rem', color: "var(--gold)" }}><Mail size={32} /></div>
                      <p style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: '0.375rem' }}>{t("lm_forgot_title")}</p>
                      <p style={{ color: "var(--muted)", fontSize: '0.75rem', marginBottom: '1rem' }}>
                        {t("lm_forgot_sub")}
                      </p>
                      <button
                        className="cta cta-ghost wide"
                        disabled={resendCountdown > 0 || forgotLoading}
                        onClick={async () => {
                          setForgotLoading(true);
                          await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim() });
                          setForgotLoading(false);
                          setResendCountdown(30);
                        }}
                      >
                        {t("lm_resend_link", resendCountdown)}
                      </button>
                      <button className="cta cta-ghost wide" style={{ marginTop: '0.5rem' }} onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(""); }}>
                        {t("lm_return")}
                      </button>
                    </div>
                  ) : (
                    <>
                      <p style={{ fontSize: '0.8125rem', color: "var(--muted)", marginBottom: '0.75rem' }}>
                        {t("lm_forgot_input")}
                      </p>
                      <input
                        className="rm-input"
                        style={{ width: "100%", marginBottom: '0.625rem', boxSizing: "border-box" }}
                        type="email"
                        placeholder={t("email_placeholder")}
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        autoFocus
                      />
                      <button
                        className="cta cta-primary wide"
                        disabled={forgotLoading || !forgotEmail.trim()}
                        onClick={async () => {
                          setForgotLoading(true);
                          await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim() });
                          setForgotSent(true);
                          setForgotLoading(false);
                        }}
                      >
                        {forgotLoading ? "..." : t("lm_send_link")}
                      </button>
                      <button className="cta cta-ghost wide" style={{ marginTop: '0.5rem' }} onClick={() => setForgotMode(false)}>
                        {t("lm_return")}
                      </button>
                    </>
                  )
                ) : (
                  <>
                    <div style={{ position: "relative" }}>
                      <input
                        ref={emailInputRef}
                        className="rm-input"
                        style={{ width: "100%", marginBottom: '0.625rem' }}
                        placeholder="Email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onFocus={() => setShowEmailSuggestions(true)}
                      />
                      {showEmailSuggestions && filteredEmails.length > 0 && (
                        <ul ref={emailSuggestionsRef} className="search-suggestions">
                          {filteredEmails.map((e) => (
                            <li key={e} className="suggestion-item">
                              <span className="suggestion-text" onMouseDown={() => { setEmail(e); setShowEmailSuggestions(false); }}>
                                {e}
                              </span>
                              <button className="suggestion-delete" onMouseDown={(ev) => { ev.stopPropagation(); removeEmail(e); }}>×</button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div style={{ position: "relative", marginBottom: '0.625rem' }}>
                      <input
                        className="rm-input"
                        style={{ width: "100%", paddingRight: '2.375rem' }}
                        type={showPwd ? "text" : "password"}
                        placeholder={t("lm_password_placeholder")}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && submit()}
                      />
                      <button type="button" onClick={() => setShowPwd(v => !v)} style={{ position: "absolute", right: '0.625rem', top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--muted)", fontSize: '1rem', padding: 0 }}>{showPwd ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg> : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}</button>
                    </div>
                    {error && <div className="error-msg" style={{ marginBottom: '0.625rem' }}>{error}</div>}
                    <button className="cta cta-primary wide" onClick={submit} disabled={loading}>
                      {loading ? "..." : t("lm_connect")}
                    </button>
                    <button
                      className="cta cta-ghost wide"
                      style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: "var(--muted)" }}
                      onClick={() => { setForgotMode(true); setForgotEmail(email); }}
                    >
                      {t("lm_forgot_password")}
                    </button>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default LoginModal;
